import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Package, Edit, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import { AddHoldingDialog } from '@/components/holdings/AddHoldingDialog';
import { EditHoldingDialog } from '@/components/holdings/EditHoldingDialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile';

interface Holding {
  id: string;
  holding_name: string;
  holding_type: string;
  description: string;
  initial_value: number;
  current_value: number;
  acquisition_date: string;
  status: string;
  notes: string;
  performance_metrics: any;
  created_at: string;
}

interface HoldingsTableProps {
  holdings: Holding[];
  loading: boolean;
  onRefresh: () => void;
}

export const HoldingsTable: React.FC<HoldingsTableProps> = ({ holdings, loading, onRefresh }) => {
  const [editingHolding, setEditingHolding] = useState<Holding | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { toast } = useToast();
  const { isMobile } = useEnhancedMobile();

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('holdings')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Holding Deleted',
        description: 'The holding has been removed from your portfolio.',
      });

      onRefresh();
    } catch (error) {
      console.error('Error deleting holding:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete holding.',
        variant: 'destructive',
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getPerformanceIndicator = (initial: number, current: number) => {
    if (initial === 0) return null;
    const change = ((current - initial) / initial) * 100;
    const isPositive = change >= 0;
    
    return (
      <div className={`flex items-center gap-1 ${isPositive ? 'text-success' : 'text-destructive'}`}>
        {isPositive ? (
          <TrendingUp className="h-3 w-3" />
        ) : (
          <TrendingDown className="h-3 w-3" />
        )}
        <span className="text-xs font-medium">
          {isPositive ? '+' : ''}{change.toFixed(1)}%
        </span>
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-success text-success-foreground';
      case 'Completed':
        return 'bg-primary text-primary-foreground';
      case 'Pending':
        return 'bg-warning text-warning-foreground';
      case 'Paused':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Holdings & Partnerships</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Holdings & Partnerships</CardTitle>
        <AddHoldingDialog onHoldingAdded={onRefresh} />
      </CardHeader>
      <CardContent>
        {holdings.length === 0 ? (
          <EmptyState
            title="No Holdings Yet"
            description="Your partnerships, investments, and deals will be displayed here once you start building your portfolio."
            icon={<Package className="w-8 h-8 text-muted-foreground" />}
            action={
              <AddHoldingDialog onHoldingAdded={onRefresh} />
            }
          />
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Initial Value</TableHead>
                    <TableHead>Current Value</TableHead>
                    <TableHead>Performance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {holdings.map((holding) => (
                    <TableRow key={holding.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <div>
                          <div className="font-semibold">{holding.holding_name}</div>
                          {holding.description && (
                            <div className="text-xs text-muted-foreground line-clamp-1">
                              {holding.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{holding.holding_type}</Badge>
                      </TableCell>
                      <TableCell>{formatCurrency(holding.initial_value)}</TableCell>
                      <TableCell>{formatCurrency(holding.current_value)}</TableCell>
                      <TableCell>
                        {getPerformanceIndicator(holding.initial_value, holding.current_value)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(holding.status)}>
                          {holding.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingHolding(holding);
                              setEditDialogOpen(true);
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(holding.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {holdings.map((holding) => (
                <Card key={holding.id} className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-base">{holding.holding_name}</h3>
                        {holding.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {holding.description}
                          </p>
                        )}
                      </div>
                      <Badge className={getStatusColor(holding.status)}>
                        {holding.status}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {holding.holding_type}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                      <div>
                        <div className="text-xs text-muted-foreground">Initial Value</div>
                        <div className="font-semibold">{formatCurrency(holding.initial_value)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Current Value</div>
                        <div className="font-semibold">{formatCurrency(holding.current_value)}</div>
                      </div>
                    </div>

                    {getPerformanceIndicator(holding.initial_value, holding.current_value) && (
                      <div className="flex items-center justify-center py-2 bg-muted/50 rounded-md">
                        {getPerformanceIndicator(holding.initial_value, holding.current_value)}
                      </div>
                    )}

                    <div className="flex items-center gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 min-h-[44px]"
                        onClick={() => {
                          setEditingHolding(holding);
                          setEditDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 min-h-[44px]"
                        onClick={() => handleDelete(holding.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </CardContent>
      
      <EditHoldingDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        holding={editingHolding}
        onHoldingUpdated={onRefresh}
      />
    </Card>
  );
};